/*
 * Authors: mariushirjanu, Koen
 * Created: 20/04/2015
 * Upgraded: 27/12/2022
 */
import * as _ from 'underscore';
import { util } from '../../../shared/util';
import { Subscription } from 'rxjs';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, Input } from '@angular/core';
import { StateService, Transition, TransitionService } from '@uirouter/angular';
import { STATE_PUNCHOUT_CART, STATE_PUNCHOUT_CATALOG_DETAIL, STATE_PUNCHOUT_CATALOG_OVERVIEW, STATE_PUNCHOUT_SHIPPING, STATE_PUNCHOUT_WEBSHOP } from '../../../shared/app-states';
import { GA1Service } from '../../../shared/ga1/ga1.service';
import { OrderEntryWebshopService } from '../entry/webshop/order-entry-webshop.service';
import { BodyClassService } from '../../../shared/services/body-class.service';
import { OrderCatalogDataService } from '../catalog/order-catalog-data.service';
import { CatalogStateService } from '../catalog/catalog-state.service';
import { PriceCartService } from '../cart/price-cart.service';
import { IWebshopLoader, OrderWebshopLoaderService } from '../entry/webshop/order-webshop-loader.service';
import { OrderCatalogService } from '../catalog/order-catalog.service';
import { PunchoutCatalogService } from '../entry/catalog/punchout-catalog.service';
import { PunchoutDataService } from './punchout-data.service';
import { IExtendedWindow } from '../../../../type_definitions/datatypes';
import { PunchoutService } from './punchout.service';
import { DialogSize, GaMessageService } from '../../../shared/message/ga-message.service';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { GaWizard } from '../../../shared/wizard/ga-wizard';
import { GaWebStorageService } from '../../../shared/services/storage/ga-web-storage.service';
import { OrderEntryWebshopComponent } from '../entry/webshop/order-entry-webshop.component';
import { OrderEntryCatalogComponent } from '../entry/catalog/order-entry-catalog.component';
import { ITranslateText } from '../../../shared/wizard/wizard-wrapper.component';
import { PunchoutProtocols } from '../entry/order-entry-data.service';
import { GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { EVENT_ORDER_BP_LIST_UPDATED, EVENT_ORDER_BP_SELECTION_UPDATED, EVENT_ORDER_COUNTRY_LIST_UPDATED, EVENT_ORDER_COUNTRY_SELECTION_UPDATED, EVENT_ORDER_EXT_CATEGORY_CHANGED, EVENT_ORDER_LOADING_CONTEXT, EVENT_ORDER_WEBSHOP_SELECTION_UPDATED } from '../../../shared/app-events';
import { SimpleTestCustomComponent } from '../../demo/ngx-formly/simple-test-custom.component';
import { OrderEntryStepId } from '../entry/order-entry-steps';

export const PUNCHOUT_WIZARD:string = 'punchout';

@Component({
  selector: 'punchout',
  template: `
    <div *ngIf="error" class="alert alert-danger"><strong>{{error}}</strong></div>

    <div *ngIf="!error" [ngClass]="{'in-frame': embedded()}">
      <wizard-wrapper class="order-content" [wizardTitle]="title" [loading]="loading" [wizardId]="wizardId">
        <div class="wizard-actions-after d-inline-block">
          <div class="float-end" *ngIf="!hideCheckoutButton">
            <a class="btn btn-primary" href="" (click)="checkout($event)" *ngIf="!hideCheckoutButton" translate>ORDERENTRY.BUTTONS.CHECKOUT</a>
            <cart-popup></cart-popup>
          </div>
        </div>
      </wizard-wrapper>
      <form class="checkoutform" method="post"></form>
    </div>
  `,
  styleUrls: ['../catalog/order-catalog.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PunchoutComponent implements OnInit, OnDestroy {
  @Input() punchoutMessageId:string;
  @Input() part:string;
  @Input() popup:boolean;

  public error:string;
  public loading:ITranslateText;
  public hideCheckoutButton:boolean;
  public wizardId:string = PUNCHOUT_WIZARD;
  private wizard:GaWizard;
  private eventHandler:Subscription;
  private onStateSuccessHook:Function;
  public embedded:Function = this.ga1Service.embedded;

  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private ga1Service:GA1Service,
    private bodyClassService:BodyClassService,
    private gaEventService:GaEventService,
    private punchoutService:PunchoutService,
    private punchoutDataService:PunchoutDataService,
    private orderCatalogDataService:OrderCatalogDataService,
    private catalogStateService:CatalogStateService,
    private priceCartService:PriceCartService,
    private messageService:GaMessageService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private orderWebshopLoaderService:OrderWebshopLoaderService,
    private wizardService:GaWizardService,
    private orderCatalogService:OrderCatalogService,
    private punchoutCatalogService:PunchoutCatalogService,
    private stateService:StateService,
    private transitionService:TransitionService,
    private webStorageService:GaWebStorageService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.activate();
    this.listen();
  }

  private activate():void {
    this.orderCatalogService.punchoutCatalog = true;
    this.punchoutService.setPunchoutMessageId(this.punchoutMessageId);
    this.punchoutCatalogService.punchoutPartNumber = this.part;

    if (!this.punchoutService.getPunchoutMessageId()) {
      this.error = 'Missing parameter: punchoutMessageId';
    } else {
      this.initWizard();

      this.orderCatalogDataService.start();
      this.catalogStateService.overviewState = STATE_PUNCHOUT_CATALOG_OVERVIEW;
      this.catalogStateService.detailState = STATE_PUNCHOUT_CATALOG_DETAIL;
      this.priceCartService.cartTab = STATE_PUNCHOUT_CART;
      this.bodyClassService.add('wizard-header');

      this.loading = {
        translateText: 'GA.LOADING',
        translateValues: { toload: 'GA.GENERAL.WEBSHOPS' }
      };
      this.orderWebshopLoaderService.start('PU', this.punchoutService.getPunchoutMessageId())
        .then(this.loadingDone);
    }
  }

  private listen():void {
    const enableDisableStepEvents:string[] = [
      EVENT_ORDER_WEBSHOP_SELECTION_UPDATED,
      EVENT_ORDER_COUNTRY_LIST_UPDATED,
      EVENT_ORDER_COUNTRY_SELECTION_UPDATED,
      EVENT_ORDER_BP_LIST_UPDATED,
      EVENT_ORDER_BP_SELECTION_UPDATED,
      EVENT_ORDER_LOADING_CONTEXT
    ];

    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      if (_.contains(enableDisableStepEvents, event.name)) {
        this.enableDisableSteps();
      }

      else if(event.name === EVENT_ORDER_EXT_CATEGORY_CHANGED){
        this.hideCheckoutButton = event.data;
        // register punchout to cart handler to execute auto-checkout when there is only one external category
        if(event.data) {
          this.punchoutCatalogService.punchoutToCartHandler(this.checkout.bind(this));
        }
      }
    });

    this.onStateSuccessHook = this.transitionService.onSuccess({
      from: STATE_PUNCHOUT_CATALOG_OVERVIEW,
      to: STATE_PUNCHOUT_CATALOG_OVERVIEW
    }, (transition:Transition) => {
      this.wizard.step(OrderEntryStepId.CATALOG).stateParams = transition.params('to');
    });
  }

  private enableDisableSteps():void {
    if (!this.orderEntryWebshopService.selectedShop || this.orderEntryWebshopService.loadingCountries || !this.orderEntryWebshopService.selectedCountry || this.orderEntryWebshopService.loadingBPs || !this.orderEntryWebshopService.selectedBP) {
      this.wizard.someEnabled(0, 'ORDERENTRY.WEBSHOP.PLEASE_SELECT_WEBSHOP_COUNTRY'); //disable all but the first step
    } else if (this.orderEntryWebshopService.loadingContext) {
      this.wizard.someEnabled(0, 'ORDERENTRY.WEBSHOP.STILL_LOADING_WEBSHOP'); //disable all but the first step
    } else {
      this.wizard.allEnabled();
    }
  }

  private loadingDone = (result:IWebshopLoader):void => {
    this.enableDisableSteps();

    if (result.single) {
      //if the selection was loaded from session storage -> do not jump
      if (!result.session && this.stateService.current.name === STATE_PUNCHOUT_WEBSHOP) {
        //skip choosing a webshop & country
        this.stateService.go(STATE_PUNCHOUT_CATALOG_OVERVIEW);
      }
    } else {
      //make people always choose a webshop & country
      this.stateService.go(STATE_PUNCHOUT_WEBSHOP, undefined, { location: 'replace' });
    }
    this.loading = undefined;
  };

  public checkout(event):Promise<void> {
    event?.preventDefault();

    if (!this.priceCartService.cartCount) {
      this.messageService.showMessageDialog(
        'ORDERENTRY.CART.EMPTY.TITLE',
        'ORDERENTRY.CART.EMPTY.MESSAGE',
        [ { label: 'GA.BUTTONS.OK' } ], undefined, undefined, DialogSize.MD, true);

      return Promise.reject()
        .catch(util.noop);
    } else {
      return this.punchoutDataService.checkout(this.punchoutService.getPunchoutMessageId())
        .then((response) => {
          let contents:string;
          const message = response.orderMessage;
          const punchoutProtocol:string = this.orderEntryWebshopService.shopContext.punchoutProtocol;

          if (punchoutProtocol === PunchoutProtocols.CXML) {
            contents = '<input name="cxml-urlencoded" type="hidden" value="' + _.escape(message) + '">';
          } else if (punchoutProtocol === PunchoutProtocols.OCI) {
            contents = message;
          } else {
            console.error('Unknown punchout protocol ', punchoutProtocol);
          }

          const url:string = this.orderEntryWebshopService.shopContext.postUrl;
          const target:string = this.orderEntryWebshopService.shopContext.postTarget || '_top';

          if (this.popup) { //launched in a popup -> send message to opener
            this.webStorageService.local.set('ga2.punchout.checkout', {
              url,
              target,
              contents
            });
            try {
              const opener = this.window.parent?.opener || this.window.opener;
              opener.punchout.checkout(url, target, contents);
            } catch (e) { // this can happen when the parameter is set accidentally
              console.error(e);
              submitCheckoutForm();
            }
          } else {
            submitCheckoutForm();
          }

          function submitCheckoutForm() {
            const checkoutFormElm = $('.checkoutform');
            checkoutFormElm
              .attr('action', url)
              .attr('target', target)
              .append(contents)
              .submit();
          }

        })
        .catch(() => {
          // go to cart tab
          this.wizard.selectStep(this.wizard.activeStepIndex + 1);

          // show the checkout button again, so user can retry themselves
          this.hideCheckoutButton = false;
        });
    }
  }

  private initWizard():void {
    this.wizard = this.wizardService.getInstance(this.wizardId);

    this.wizard.steps([
      this.wizardService.createStep('webshop', 'ORDERENTRY.WEBSHOP.TITLE', OrderEntryWebshopComponent, STATE_PUNCHOUT_WEBSHOP, undefined, undefined, true),
      this.wizardService.createStep('catalog', 'CATALOG.TITLE', OrderEntryCatalogComponent, STATE_PUNCHOUT_CATALOG_OVERVIEW, undefined, [ STATE_PUNCHOUT_CATALOG_DETAIL ], true),
      this.wizardService.createStep('cart', 'ORDERENTRY.CART.TITLE', SimpleTestCustomComponent, STATE_PUNCHOUT_CART, undefined, undefined, true),
      this.wizardService.createStep('shipping', 'ORDERENTRY.SHIPPING.TITLE', SimpleTestCustomComponent, STATE_PUNCHOUT_SHIPPING, undefined, undefined, true)
    ]);
    this.wizard.sync();
  }

  public get title():string {
    return this.orderEntryWebshopService.title;
  }

  public ngOnDestroy = ():void => {
    this.wizard.clear();
    this.punchoutService.clear();
    this.catalogStateService.clear();
    this.orderCatalogService.clear();
    this.bodyClassService.remove('wizard-header');
    this.orderWebshopLoaderService.clear();
    this.onStateSuccessHook();
  };
}